import type { UIRouterGlobals } from "@uirouter/core";
import type { IController, IScope } from "angular";
import { isNil, noop, remove } from "lodash";
import type {
    IAvailableChannel,
    IEquipmentDetails,
    IResponseWithLoading,
    ISiteEquipmentBase,
    ISiteEquipmentWithCoverages,
} from "App/Modules/Models";
import { EquipmentClass } from "App/Modules/Models/Models/EquipmentClass";
import type { ICredentials } from "App/Modules/Models/Models/ICredentials";
import type { IDirectConnection } from "App/Modules/Models/Models/IDirectConnection";
import type { ISiteEquipmentRepository } from "App/Modules/Repositories/SiteEquipment/ISiteEquipmentRepository";
import type { IModalService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import { Subject, zip } from "rxjs";
import { filter, finalize, switchMap } from "rxjs/operators";
import type ISiteEquipmentListService from "../../../../../Services/EquipmentList/ISiteEquipmentListService";

export default class EquipmentDetailsComponentController implements IController {
    public static $inject: string[] = [
        "$scope",
        "mmModalService",
        "mmSiteEquipmentRepository",
        "$uiRouterGlobals",
        "$translate",
        "irSiteEquipmentListService",
    ];

    public siteEquipment: ISiteEquipmentWithCoverages;

    public isSidebarCollapsed: boolean;

    public siteId: string;

    public directConnectionsResponse: IResponseWithLoading<IDirectConnection[]>;

    public availableChannelsResponse: IResponseWithLoading<IAvailableChannel[]>;

    public directConnections: IDirectConnection[];

    public availableChannels: IAvailableChannel[];

    public directConnectionDeleteResponse: {
        [id: string]: IResponseWithLoading<void>;
    };

    private siteEquipmentChangeSubject: Subject<ISiteEquipmentWithCoverages>;

    constructor(
        private readonly $scope: IScope,
        private readonly modalService: IModalService,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
        private readonly $uiRouterGlobals: UIRouterGlobals,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly siteEquipmentListService: ISiteEquipmentListService,
    ) {
        this.siteId = this.$uiRouterGlobals.params.siteId;
        this.directConnectionDeleteResponse = {};
    }

    public $onInit() {
        this.siteEquipmentChangeSubject = new Subject<ISiteEquipmentWithCoverages>();

        this.$scope.$watch("$ctrl.siteEquipment", (eq: ISiteEquipmentWithCoverages) =>
            this.siteEquipmentChangeSubject.next(eq),
        );

        this.$scope.$watch("$ctrl.siteEquipment.type", () => this.siteEquipmentChangeSubject.next(this.siteEquipment));

        this.siteEquipmentChangeSubject
            .pipe(
                filter(
                    (siteEquipment: ISiteEquipmentWithCoverages) => !isNil(siteEquipment) && !isNil(siteEquipment.type),
                ),
                switchMap((siteEquipment: ISiteEquipmentWithCoverages) =>
                    zip(
                        extendWithLoadingAndFail(
                            this.siteEquipmentRepository.getDirectConnections(this.siteId, siteEquipment.id),
                        ),
                        extendWithLoadingAndFail(
                            this.siteEquipmentRepository.getAvailableChannels(this.siteId, siteEquipment.id),
                        ),
                    ),
                ),
                finalize(() => this.$scope.$applyAsync()),
            )
            .subscribe(
                ([directConnections, availableChannels]: [
                    IResponseWithLoading<IDirectConnection[]>,
                    IResponseWithLoading<IAvailableChannel[]>,
                ]) => {
                    if (!isNil(directConnections)) {
                        this.directConnectionsResponse = directConnections;
                        this.availableChannelsResponse = availableChannels;

                        if (!directConnections.isLoading && !availableChannels.isLoading) {
                            this.directConnections = directConnections.data;
                            this.availableChannels = availableChannels.data;
                        }
                    }
                },
                ([directConnections, availableChannels]) => {
                    this.directConnectionsResponse = directConnections;
                    this.availableChannelsResponse = availableChannels;
                },
            );
    }

    public editCredentials() {
        this.modalService
            .open<IEquipmentDetails, ISiteEquipmentWithCoverages>(
                "irEditEquipmentCredentials",
                this.siteEquipment.equipment,
                this.siteEquipment,
            )
            .subscribe(
                (response: IEquipmentDetails) => {
                    this.siteEquipment.equipment = response;
                },
                (reason) => reason,
            );
    }

    public editCommunicationConfiguration() {
        this.modalService
            .open<IEquipmentDetails, ISiteEquipmentWithCoverages>(
                "irEditCommunicationConfiguration",
                this.siteEquipment.equipment,
                this.siteEquipment,
            )
            .subscribe(
                (response: IEquipmentDetails) => {
                    this.siteEquipment.equipment = response;
                },
                (reason) => reason,
            );
    }

    // ToolTip cashing inner html. We need to override that behaviour
    public getToolTipHTML(credentials: ICredentials) {
        return `<i class='fal fa-key'></i> ${credentials.password}`;
    }

    public getDirectConnectionText(directConnection: IDirectConnection) {
        const directConnectionType = this.$translate.instant(
            `Entities.DirectConnectionType.Enum${directConnection.directConnectionTypeName}`,
        );
        const outputChannel = directConnection.outputChannel.name;
        const inputChannel = directConnection.inputChannel.name;

        return `${outputChannel} - ${inputChannel} (${directConnectionType})`;
    }

    public getDirectConnectionHeader(directConnection: IDirectConnection) {
        return `${directConnection.outputSiteEquipment.name} - ${directConnection.inputSiteEquipment.name}`;
    }

    public editDirectConnections() {
        const siteEquipmentBaseEntity = {
            class: this.siteEquipment.class,
            className: this.siteEquipment.className,
            icon: this.siteEquipment.icon,
            id: this.siteEquipment.id,
            name: this.siteEquipment.name,
            type: this.siteEquipment.type,
        } as ISiteEquipmentBase;

        this.modalService
            .open<ISiteEquipmentBase, IAvailableChannel[], { dc: IDirectConnection; ac: IAvailableChannel }>(
                "irEditDirectConnections",
                siteEquipmentBaseEntity,
                this.availableChannels,
            )
            .subscribe(({ dc, ac }) => {
                this.attachVideoStreamsToAnalogCamera(dc);
                this.directConnections.push(dc);

                remove(this.availableChannels, ac);
            }, noop);
    }

    public deleteDirectConnection(directConnection: IDirectConnection) {
        extendWithLoadingAndFail(this.siteEquipmentRepository.deleteDirectConnection(this.siteId, directConnection.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<void>) => {
                    this.directConnectionDeleteResponse[directConnection.id] = response;

                    if (!response.isLoading) {
                        this.directConnections = this.directConnections.filter((dc) => dc.id !== directConnection.id);

                        this.releaseChannel(directConnection);
                        this.detachVideoStreams(directConnection);
                    }
                },
                (error: IResponseWithLoading<void>) => {
                    this.directConnectionDeleteResponse[directConnection.id] = error;
                },
            );
    }

    public isDeleteTooltipVisible(directConnection: IDirectConnection) {
        if (
            directConnection.inputSiteEquipment.class !== EquipmentClass.VideoConverter ||
            directConnection.outputSiteEquipment.class !== EquipmentClass.AnalogCamera
        ) {
            return false;
        }

        const analogCamera = this.siteEquipmentListService.getSiteEquipment(directConnection.outputSiteEquipment.id);

        return analogCamera.functionalities.length > 0;
    }

    private detachVideoStreams(directConnection: IDirectConnection) {
        if (
            directConnection.inputSiteEquipment.class !== EquipmentClass.VideoConverter ||
            directConnection.outputSiteEquipment.class !== EquipmentClass.AnalogCamera
        ) {
            return;
        }

        const siteEquipmentWithVideoStreams = this.siteEquipmentListService.getSiteEquipment(
            directConnection.outputSiteEquipment.id,
        );

        siteEquipmentWithVideoStreams.videoStreams = [];

        siteEquipmentWithVideoStreams.viewCoverages.forEach((vc) => {
            vc.functionalities.forEach((f) => {
                f.videoStream = null;
            });
        });
    }

    private attachVideoStreamsToAnalogCamera(directConnection: IDirectConnection) {
        if (directConnection.inputSiteEquipment.class === EquipmentClass.AnalogCamera) {
            this.refreshVideoStreamsOnAnalogCamera(directConnection.inputSiteEquipment);
        }

        if (directConnection.outputSiteEquipment.class === EquipmentClass.AnalogCamera) {
            this.refreshVideoStreamsOnAnalogCamera(directConnection.outputSiteEquipment);
        }
    }

    private refreshVideoStreamsOnAnalogCamera(analogCameraSiteEquipment: ISiteEquipmentBase) {
        this.siteEquipmentListService.refreshSiteEquipment(
            analogCameraSiteEquipment.siteFriendlyId,
            analogCameraSiteEquipment.id,
        );
    }

    private releaseChannel(directConnection: IDirectConnection) {
        if (this.siteEquipment.id === directConnection.outputSiteEquipment.id) {
            this.availableChannels.push(directConnection.outputChannel);
        } else if (this.siteEquipment.id === directConnection.inputSiteEquipment.id) {
            this.availableChannels.push(directConnection.inputChannel);
        }
    }
}
